import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Tractor } from "lucide-react";
import { api } from "../api";
import { reportSwallowed } from "../lib/reportSwallowed";
import { ApiError } from "../api/client";
import { qk } from "../hooks/queries";
import { useJobs } from "../hooks/useJobTracker";
import { JdReconsentNote } from "./JdReconsentNote";
import type { DashboardField } from "../types/contract";

// For a LINKED field: two primary options — "Pull yield" (all yield layers) and "Pull everything" —
// plus the list of available machine events (harvest / tillage / application / seeding) with
// checkboxes for a selective pull. The backend tracks which JD files belong to each layer and
// GENERATES the export for layers with no known file yet (`has_file:false`).
function pullEstimate(layerCount: number): string {
  if (layerCount <= 2) return "~2–5 min";
  if (layerCount <= 6) return "~5–15 min";
  return "~15–30 min";
}

export function JdMachineLayers({
  field,
  onClose,
}: {
  field: DashboardField;
  onClose?: () => void;
}) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [noteErr, setNoteErr] = useState(false);

  const {
    data: ops,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["jd-operations", field.field_id],
    queryFn: () => api.jdOperations(field.field_id),
  });
  const yieldCount = useMemo(
    () => (ops ?? []).filter((o) => o.kind === "harvest").length,
    [ops],
  );
  // A flat list is then indistinguishable from one JD field's — "Fall '25 harvest" twice reads as a
  // duplicate rather than as two real layers on two halves of the ground. Triggered off the LAYERS
  // rather than the link count, so a link contributing nothing adds no noise.
  const jdFieldIds = useMemo(
    () =>
      [
        ...new Set((ops ?? []).map((o) => o.jd_field_id).filter(Boolean)),
      ] as string[],
    [ops],
  );
  const multiJd = jdFieldIds.length > 1;
  const jdLabel = (id?: string) =>
    id ? `JD field ${jdFieldIds.indexOf(id) + 1}` : "";

  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  async function pull(
    scope: { mode: "yield" | "all" } | { operation_ids: string[] },
  ) {
    setBusy(true);
    setNote(null);
    setNoteErr(false);
    try {
      const job = await api.jdPullOperations(field.field_id, scope);
      // machine data changed -> fold it into the covariate stack (standing auto-trigger)
      void api
        .buildCovariates(field.field_id)
        .catch((err) => reportSwallowed("build covariates", err));
      void qc.invalidateQueries({ queryKey: qk.fields });
      const layerCount =
        "mode" in scope
          ? scope.mode === "yield"
            ? yieldCount || 1
            : ops?.length || 1
          : scope.operation_ids.length;
      const what =
        "mode" in scope
          ? scope.mode === "yield"
            ? `${layerCount} yield layer${layerCount === 1 ? "" : "s"}`
            : `all ${layerCount} machine layers`
          : `${layerCount} selected layer${layerCount === 1 ? "" : "s"}`;
      // ONE JOB PER JD FIELD: a merged field carrying two links files two pulls.
      const jobIds = job.job_ids?.length
        ? job.job_ids
        : job.job_id
          ? [job.job_id]
          : [];
      jobIds.forEach((id, i) =>
        useJobs.getState().upsert({
          job_id: id,
          pct: 0,
          status: "running",
          message: `Generating ${what}${jobIds.length > 1 ? ` — JD field ${i + 1} of ${jobIds.length}` : ""} (${pullEstimate(layerCount)})`,
        }),
      );
      setPicked(new Set());
      onClose?.();
    } catch (e) {
      setNote(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Something went wrong.",
      );
      setNoteErr(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Tractor size={14} className="text-muted" />
        <button
          disabled={busy || isLoading}
          className="rounded bg-accent px-2.5 py-1 text-xs font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
          onClick={() => void pull({ mode: "yield" })}
        >
          Pull yield{yieldCount ? ` (${yieldCount})` : ""}
        </button>
        <button
          disabled={busy || isLoading}
          className="rounded border border-line px-2.5 py-1 text-xs text-fg hover:bg-hover disabled:opacity-50"
          onClick={() => void pull({ mode: "all" })}
        >
          Pull everything{ops?.length ? ` (${ops.length})` : ""}
        </button>
        {picked.size > 0 && (
          <button
            disabled={busy}
            className="rounded border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1 text-xs text-emerald-300 hover:bg-emerald-400/20 disabled:opacity-50"
            onClick={() => void pull({ operation_ids: [...picked] })}
          >
            Pull selected ({picked.size})
          </button>
        )}
        {busy && <Loader2 size={12} className="animate-spin text-accent" />}
      </div>

      <JdReconsentNote />

      {/*
 A field CAN carry several JD links — `UNIQUE (field_id, jd_field_id)` is per FIELD — and a
          combined field normally does, one from each half. Saying so up front is what stops the
          layer list below reading as duplicates, and sets the expectation that a pull covers both.
*/}
      {(field.jd_links?.length ?? 0) > 1 && (
        <span className="text-[11px] text-muted">
          Linked to {field.jd_links!.length} John Deere fields — pulling brings
          in machine data from all of them.
        </span>
      )}

      {note && (
        <span
          className={`text-[11px] ${noteErr ? "text-red-400" : "text-muted"}`}
        >
          {note}
        </span>
      )}
      {isLoading && (
        <span className="text-[11px] text-muted">
          Loading the field's machine events…
        </span>
      )}
      {error && (
        <span className="text-[11px] text-red-400">
          {(error as Error).message}
        </span>
      )}

      {ops && ops.length > 0 && (
        <ul className="space-y-0.5">
          {ops.map((o) => (
            <li key={o.operation_id}>
              <label className="flex cursor-pointer items-center gap-1.5 text-[11px]">
                <input
                  type="checkbox"
                  checked={picked.has(o.operation_id)}
                  onChange={() => toggle(o.operation_id)}
                  aria-label={o.label}
                />
                <span className="truncate">{o.label}</span>
                <span className="rounded bg-white/5 px-1 text-[9px] text-faint">
                  {o.kind}
                </span>
                {/*
 WHICH JD field, only when there is more than one to tell apart. The id itself is
                    a GUID and never shown — it rides the tooltip so support can still read it.
*/}
                {multiJd && o.jd_field_id && (
                  <span
                    title={o.jd_field_id}
                    className="rounded bg-amber-400/10 px-1 text-[9px] text-amber-300"
                  >
                    {jdLabel(o.jd_field_id)}
                  </span>
                )}
                {o.date && (
                  <span className="text-[9px] text-faint">{o.date}</span>
                )}
                {!o.has_file && (
                  <span
                    title="No JD file is tracked for this layer yet — pulling it generates the export first."
                    className="rounded bg-sky-400/10 px-1 text-[9px] text-sky-300"
                  >
                    will generate
                  </span>
                )}
              </label>
            </li>
          ))}
        </ul>
      )}
      {ops && ops.length === 0 && (
        <span className="text-[11px] text-muted">
          No machine events found on the linked JD field.
        </span>
      )}
    </div>
  );
}
